import React, { useEffect, useRef, useState } from "react";
import { Card } from "semantic-ui-react";
import { useDispatch } from "react-redux";
import { t } from "i18next";

import useTheme from "../../../../hooks/useTheme";
import { logUserAction } from "../../../../utils/analyticsFunctions";
import UserActions from "../../../../utils/Constants/UserActions";
import { INLINE_APPLICATION_CONTACT_STEP_ID } from "../../../../utils/Constants/applicationStepTracking";
import ApplicationContactForm from "../../../Candidate/ApplyCandidate/Components/ApplicationContactForm";
import ApplicationCompensationSummary from "../../../Candidate/ApplyCandidate/Components/ApplicationCompensationSummary";
import { showToast as showToastAction } from "../../../../redux/Snackbar/SnackbarActions";
import { UserAvatar } from "../../../UI/DisplayImage";
import { AppColumn, AppRow } from "../../../UI/Layouts";
import { SizedBox } from "../../../UI/Sizes";
import AppText from "../../../UI/Texts/AppTexts";

const InlineApplicationSection = React.forwardRef(
  (
    {
      jobData,
      auth,
      candidateData,
      contactPerson,
      contactStatus,
      ensureContactPerson,
      eligibleContactPeople = [],
      canChangeContactPerson,
      onSelectContactPerson,
      applicationPrefill,
      isPrefillLoading,
      applicationSessionId,
      onUpdateCandidateData,
      onSubmitted,
    },
    forwardedRef,
  ) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const localRef = useRef(null);
    const trackedView = useRef(false);
    const [isContactSelectorOpen, setIsContactSelectorOpen] = useState(false);

    useEffect(() => {
      const element = localRef.current;
      if (!element || trackedView.current || typeof IntersectionObserver === "undefined") {
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting || trackedView.current) return;
          trackedView.current = true;
          const analyticsPayload = {
            application_session_id: applicationSessionId,
            application_step_id: INLINE_APPLICATION_CONTACT_STEP_ID,
            application_step_name: UserActions.application_contact_form,
            application_step_index: 1,
            application_step_event: "viewed",
            application_source: "merged_job_page",
          };
          logUserAction(UserActions.application_step_viewed, {
            jobId: jobData.jobId,
            analyticsPayload,
          });
          logUserAction(UserActions.application_contact_form, {
            jobId: jobData.jobId,
            analyticsPayload,
          });
          observer.disconnect();
        },
        { threshold: 0.2 },
      );
      observer.observe(element);
      return () => observer.disconnect();
    }, [applicationSessionId, jobData.jobId]);

    const setRefs = (element) => {
      localRef.current = element;
      if (typeof forwardedRef === "function") forwardedRef(element);
      else if (forwardedRef) forwardedRef.current = element;
    };

    const showContactSkeleton =
      ["idle", "loading"].includes(contactStatus) &&
      !contactPerson?.userId;
    const isApplicationUnavailable =
      contactStatus === "empty" && !isPrefillLoading;
    const selectedContactIdentity = contactPerson?.userId ?
      `${contactPerson.userId}:${contactPerson.workplaceId ?? ""}` : "";

    return (
      <div ref={setRefs}>
        <Card
          fluid
          style={{
            borderRadius: theme.radius.default,
            border: `1px solid ${theme.colors.lightGrey}`,
            boxShadow: "0 10px 24px rgba(0, 0, 0, 0.10)",
          }}
        >
          <div
            style={{
              padding: `${theme.spacing.compactElementPadding} ${theme.spacing.contentElementPadding}`,
            }}
          >
            <AppText.SubHeading
              use="h2"
              fontWeight={700}
              margins={{ marginBottom: "0px" }}
            >
              {t("jobdetail.application.title")}
            </AppText.SubHeading>
            <SizedBox height={theme.spacing.elementPaddingSmall} />
            <AppText.FootnoteSmall color={theme.colors.textSecondary}>
              {t("jobdetail.application.non_binding")}
            </AppText.FootnoteSmall>
            <SizedBox height={theme.spacing.compactElementPadding} />

            <ApplicationCompensationSummary jobData={jobData} compact />

            {showContactSkeleton ? (
              <AppText.FootnoteSmall
                role="status"
                color={theme.colors.textSecondary}
              >
                {t("jobdetail.button.loading_contact_person")}
              </AppText.FootnoteSmall>
            ) : contactPerson?.userId ? (
              <AppRow crossAxisAlignment="center" style={{ gap: "10px" }}>
                <UserAvatar
                  url={contactPerson.profileImageUrl}
                  name={`${contactPerson.firstName ?? ""} ${contactPerson.lastName ?? ""}`}
                  style={{
                    width: theme.sizing.applicationContactAvatarCompact,
                    height: theme.sizing.applicationContactAvatarCompact,
                  }}
                />
                <AppColumn style={{ minWidth: 0 }}>
                  <AppText.Footnote fontWeight={600}>
                    {[contactPerson.firstName, contactPerson.lastName]
                      .filter(Boolean)
                      .join(" ") || t("jobdetail.application.recruiter")}
                  </AppText.Footnote>
                  {contactPerson.workplaceName && (
                    <AppText.FootnoteSmall color={theme.colors.textSecondary}>
                      {contactPerson.workplaceName}
                    </AppText.FootnoteSmall>
                  )}
                </AppColumn>
                {canChangeContactPerson && (
                  <button
                    type="button"
                    aria-expanded={isContactSelectorOpen}
                    onClick={() => setIsContactSelectorOpen((open) => !open)}
                    style={{
                      minHeight: "44px",
                      marginLeft: "auto",
                      padding: "8px",
                      border: 0,
                      background: "transparent",
                      color: theme.primaryColor,
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    {t("jobdetail.application.change_recruiter")}
                  </button>
                )}
              </AppRow>
            ) : null}

            {canChangeContactPerson && isContactSelectorOpen && (
              <>
                <SizedBox height={theme.spacing.elementPaddingSmall} />
                <label>
                  <AppText.FootnoteSmall color={theme.colors.textSecondary}>
                    {t("jobdetail.application.select_recruiter")}
                  </AppText.FootnoteSmall>
                  <select
                    value={selectedContactIdentity}
                    onChange={(event) => {
                      const nextContact = eligibleContactPeople.find(
                        (person) =>
                          `${person.userId}:${person.workplaceId ?? ""}` ===
                          event.target.value,
                      );
                      if (nextContact) onSelectContactPerson(nextContact);
                      setIsContactSelectorOpen(false);
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      minHeight: "44px",
                      marginTop: theme.spacing.elementPaddingSmall,
                      padding: "0 12px",
                      border: `1px solid ${theme.colors.lightGrey}`,
                      borderRadius: theme.radius.small,
                      background: theme.colors.white,
                      color: theme.colors.dark,
                    }}
                  >
                    {eligibleContactPeople.map((person) => (
                      <option
                        key={`${person.userId}:${person.workplaceId ?? ""}`}
                        value={`${person.userId}:${person.workplaceId ?? ""}`}
                      >
                        {[person.firstName, person.lastName]
                          .filter(Boolean)
                          .join(" ")}
                        {person.workplaceName ?
                          ` – ${person.workplaceName}` : ""}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}

            {(showContactSkeleton || contactPerson?.userId) && (
              <SizedBox height={theme.spacing.compactElementPadding} />
            )}

            {isApplicationUnavailable ? (
              <AppText.Caption
                color={theme.colors.textSecondary}
                role="status"
              >
                {t("candidatedetail.form_error.no_contact_person")}
              </AppText.Caption>
            ) : (
              <ApplicationContactForm
                jobData={jobData}
                contactPerson={contactPerson}
                ensureContactPerson={ensureContactPerson}
                candidateData={candidateData}
                auth={auth}
                applicationPrefill={applicationPrefill}
                compact
                onUpdateCandidateData={onUpdateCandidateData}
                onSubmitted={(result) => {
                  logUserAction(UserActions.application_step_cta_clicked, {
                    jobId: jobData.jobId,
                    analyticsPayload: {
                      application_session_id: applicationSessionId,
                      current_application_step: INLINE_APPLICATION_CONTACT_STEP_ID,
                      application_source: "merged_job_page",
                    },
                  });
                  onSubmitted(result);
                }}
                showToast={(message, options) =>
                  dispatch(showToastAction(message, options))
                }
              />
            )}

          </div>
        </Card>
      </div>
    );
  },
);

InlineApplicationSection.displayName = "InlineApplicationSection";

export default InlineApplicationSection;
